import { useCallback, useMemo, useState, type UIEvent } from 'react';

import { useDebounce } from 'ahooks';

import { FN_OPTIONS } from 'application/consts';
import { useAccountCompanyId } from 'application/hooks';
import useDropdownPlacement from 'application/hooks/useDropdownPlacement';
import { usePlayerContext } from 'application/providers/PlayerProvider';

import {
  AdCampaignStatusesEnum,
  PromoClipsQuerySort,
  type MediaPromoClipsQueryVariables,
} from '@shared/api/graphql/generated';

import { usePromoClipsList } from '../../ReportRequestForm/PromoSelector/usePromoClipsList';
import { usePromoDropdownControls } from '../../ReportRequestForm/PromoSelector/usePromoDropdownControls';
import type { PromoListItem } from '../../ReportRequestForm/PromoSelector/types';

import type { DailyPromoSelectProps } from './types';

export const useDailyPromoSelectViewModel = ({ mediafileId, onChange }: DailyPromoSelectProps) => {
  const companyId = useAccountCompanyId();
  const { fileUuid, isLoading, isPaused, pause, play, resume } = usePlayerContext();
  const {
    containerRef,
    filterButtonRef,
    filterContainerRef,
    isDropdownOpen,
    isFilterOpen,
    triggerRef,
    onDropdownToggle,
    onFilterToggle,
  } = usePromoDropdownControls();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState(PromoClipsQuerySort.CreatedDesc);
  const [selectedStatuses, setSelectedStatuses] = useState<AdCampaignStatusesEnum[]>([]);
  const [selectedPromoSnapshot, setSelectedPromoSnapshot] = useState<PromoListItem | null>(null);
  const searchQuery = useDebounce(search.trim(), FN_OPTIONS);
  const filters = useMemo<MediaPromoClipsQueryVariables['filters'] | undefined>(() => {
    const nextFilters = {
      ...(searchQuery ? { query: searchQuery } : {}),
      ...(selectedStatuses.length ? { statuses: selectedStatuses } : {}),
    };

    return Object.keys(nextFilters).length ? nextFilters : undefined;
  }, [searchQuery, selectedStatuses]);
  const queryKey = useMemo(
    () => [companyId, searchQuery, sort, selectedStatuses.join(',')].join('|'),
    [companyId, searchQuery, selectedStatuses, sort]
  );
  const { hasNextPage, isInitialLoading, isNextPageLoading, items, queryError, statusesInfo, onLoadMore, onRetry } =
    usePromoClipsList(companyId, undefined, filters, false, isDropdownOpen, queryKey, sort, true);
  const dropdownState = useDropdownPlacement({
    containerRef: triggerRef,
    isOpen: isDropdownOpen,
    repositionKey: `${queryKey}:${items.length}:${isInitialLoading}`,
    gap: 4,
    maxHeight: 350,
    minHeight: 160,
    preferredHeight: 350,
    viewportGap: 16,
  });
  const selectedPromo = useMemo(
    () =>
      mediafileId
        ? items.find((item) => item.mediafileId === mediafileId) ||
          (selectedPromoSnapshot?.mediafileId === mediafileId ? selectedPromoSnapshot : undefined)
        : undefined,
    [items, mediafileId, selectedPromoSnapshot]
  );

  const onStatusChange = useCallback((status: AdCampaignStatusesEnum, isSelected: boolean) => {
    setSelectedStatuses((currentStatuses) => {
      if (isSelected) {
        return currentStatuses.includes(status) ? currentStatuses : [...currentStatuses, status];
      }

      return currentStatuses.filter((currentStatus) => currentStatus !== status);
    });
  }, []);
  const onSortToggle = useCallback(() => {
    setSort((currentSort) =>
      currentSort === PromoClipsQuerySort.CreatedDesc ? PromoClipsQuerySort.CreatedAsc : PromoClipsQuerySort.CreatedDesc
    );
  }, []);
  const onSelectPromo = useCallback(
    (promo: PromoListItem) => {
      if (!promo.mediafileId) {
        return;
      }

      setSelectedPromoSnapshot(promo);
      onChange(promo.mediafileId);
      onDropdownToggle();
    },
    [onChange, onDropdownToggle]
  );
  const onPromoPlaybackToggle = useCallback(
    (promo: PromoListItem) => {
      if (!promo.audioUrl) {
        return;
      }

      if (fileUuid === promo.id && !isPaused) {
        pause();

        return;
      }

      if (fileUuid === promo.id) {
        resume();

        return;
      }

      play({ fileUuid: promo.id, fileName: promo.title, fileUrl: promo.audioUrl });
    },
    [fileUuid, isPaused, pause, play, resume]
  );
  const onListScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => {
      const element = event.currentTarget;

      if (hasNextPage && element.scrollTop + element.clientHeight >= element.scrollHeight - 24) {
        onLoadMore();
      }
    },
    [hasNextPage, onLoadMore]
  );

  return {
    containerRef,
    dropdownState,
    fileUuid,
    filterButtonRef,
    filterContainerRef,
    isDropdownOpen,
    isFilterOpen,
    isInitialLoading,
    isLoading,
    isPaused,
    isNextPageLoading,
    items,
    queryError,
    search,
    selectedPromo,
    selectedStatuses,
    sort,
    statusesInfo,
    triggerRef,
    onListScroll,
    onDropdownToggle,
    onFilterToggle,
    onPromoPlaybackToggle,
    onRetry,
    onSearchChange: setSearch,
    onSelectPromo,
    onSortToggle,
    onStatusChange,
  };
};
